import {
  Button,
  ButtonColorEnum,
  ButtonSizeEnum,
  ButtonTypesEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  FloatInputForm,
  FloatInputPositionEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  SaveIcon,
  SimilarIcon,
  Tooltip,
  TrashDeleteIcon,
  useWindowWidth,
} from '@acceso/design-system';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { MentionFilterSegmentLogo } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentLogos';
import { MentionFilterSegmentPeople } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentPeople';
import { MentionFilterSegmentSupports } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentSupports';

import { useTimeKeepingContext } from '../context/TimeKeepingContext';

const NONE_SESSION_VALUE = '__none__';

export function BroadcastScheduleProgramBodySegmentToolkitTimeKeepingToolbar() {
  const {
    selectedLogos,
    selectedPeople,
    sessionSupports,
    sessions,
    activeSessionId,
    onLogosChange,
    onPeopleChange,
    onSupportsChange,
    onSaveSession,
    onLoadSession,
    onClearSession,
    onDeleteSession,
    onAutoSort,
  } = useTimeKeepingContext();

  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const { width } = useWindowWidth();

  const sessionBtnRef = useRef<HTMLButtonElement | null>(null);
  const [openSessionSelector, setOpenSessionSelector] = useState(false);
  const [sessionDropdownCoords, setSessionDropdownCoords] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [sessionSearchText, setSessionSearchText] = useState('');
  const [selectedSessionName, setSelectedSessionName] = useState<string | null>(null);
  const [sessionNameInput, setSessionNameInput] = useState<string>('');
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (openSessionSelector && sessionBtnRef.current) {
      const rect = sessionBtnRef.current.getBoundingClientRect();
      setSessionDropdownCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
  }, [openSessionSelector]);

  useEffect(() => {
    if (!activeSessionId) {
      setSelectedSessionName(null);
      setSessionNameInput('');
      return;
    }
    const session = sessions.find(s => s.id === activeSessionId);
    if (session) {
      setSelectedSessionName(session.name);
      setSessionNameInput(session.name);
    }
  }, [activeSessionId, sessions]);

  const handleToggle = (filter: string) => {
    setOpenFilter(openFilter === filter ? null : filter);
  };

  const sessionOptions = [
    { key: NONE_SESSION_VALUE, value: NONE_SESSION_VALUE, label: t('Ninguna') },
    ...sessions.map(s => ({ key: s.id, value: s.id, label: s.name })),
  ];

  const handleSelectSession = (value: ListboxSelected) => {
    const sessionId = value[0];
    if (sessionId === NONE_SESSION_VALUE) {
      setSelectedSessionName(null);
      setSessionNameInput('');
      onClearSession();
      setOpenSessionSelector(false);
      setSessionSearchText('');
      return;
    }
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    setSelectedSessionName(session.name);
    setSessionNameInput(session.name);
    onLoadSession(session);
    setOpenSessionSelector(false);
    setSessionSearchText('');
  };

  const handleSaveCurrentAsSession = () => {
    const trimmed = sessionSearchText.trim();
    onSaveSession(trimmed);
    setSelectedSessionName(trimmed);
    setOpenSessionSelector(false);
    setSessionSearchText('');
  };

  const isNewSession =
    sessionSearchText.trim() !== '' && !sessions.find(s => s.name === sessionSearchText.trim());

  const destroyActiveSession = () => {
    const session = sessions.find(s => s.name === selectedSessionName);
    if (!session) return;
    onDeleteSession(session.id);
    setSelectedSessionName(null);
    setSessionNameInput('');
  };

  const autoSortCards = () => {
    onAutoSort();
  };

  return (
    <div
      data-id="time-keeping-toolbar"
      className="mb-3 mt-2 flex flex-row items-center justify-between gap-14 border-b border-neutral-30 pb-3"
    >
      <div className="flex flex-row items-center gap-2">
        <MentionFilterSegmentLogo
          isOpen={openFilter === 'SegmentLogo'}
          onToggleFilter={() => handleToggle('SegmentLogo')}
          showLabel={width < 768 || width > 1680}
          onClose={() => setOpenFilter(null)}
          className="!border-white"
          value={selectedLogos}
          onChange={onLogosChange}
          showLabelFilter={true}
        />
        <MentionFilterSegmentPeople
          isOpen={openFilter === 'SegmentPeople'}
          onToggleFilter={() => handleToggle('SegmentPeople')}
          showLabel={width < 768 || width > 1680}
          onClose={() => setOpenFilter(null)}
          className="!border-white"
          value={selectedPeople}
          onChange={onPeopleChange}
        />
        <MentionFilterSegmentSupports
          isOpen={openFilter === 'SegmentSupports'}
          onToggleFilter={() => handleToggle('SegmentSupports')}
          showLabel={width < 768 || width > 1680}
          onClose={() => setOpenFilter(null)}
          className="!border-white"
          value={sessionSupports}
          onChange={onSupportsChange}
        />
      </div>
      <div className="flex flex-1 flex-row items-center justify-end gap-2">
        <div className="flex flex-row items-center gap-2">
          {/* Session selector */}
          <Button
            ref={sessionBtnRef}
            size={ButtonSizeEnum.SM}
            type={ButtonTypesEnum.BUTTON}
            color={ButtonColorEnum.SECONDARY_OLD}
            rightIcon={() => (openSessionSelector ? <ChevronUpIcon /> : <ChevronDownIcon />)}
            onClick={() => setOpenSessionSelector(prev => !prev)}
            className="text-left"
          >
            <span className="line-clamp-2 overflow-hidden">
              {selectedSessionName ?? 'Sesión de trabajo'}
            </span>
          </Button>
          {openSessionSelector &&
            sessionDropdownCoords &&
            createPortal(
              <div
                className="session__selector fixed z-100 min-w-[300px] max-w-[400px] border border-neutral-20 bg-white p-4 shadow-lg"
                style={{ top: sessionDropdownCoords.top, right: sessionDropdownCoords.right }}
              >
                <Listbox
                  dark={false}
                  mode="single"
                  noSort
                  items={sessionOptions}
                  onChange={handleSelectSession}
                  identifiers={{ key: 'value', value: 'value', label: 'label' }}
                  selected={(() => {
                    if (!selectedSessionName) return [NONE_SESSION_VALUE];
                    const match = sessions.find(s => s.name === selectedSessionName);
                    return match ? [match.id] : [NONE_SESSION_VALUE];
                  })()}
                  onSearch={setSessionSearchText}
                />
                {isNewSession && (
                  <button
                    className="flex w-full items-center gap-2 border-t border-neutral-20 px-3 py-2 text-left text-sm text-text-dark-10 hover:bg-neutral-10"
                    onClick={handleSaveCurrentAsSession}
                  >
                    <SaveIcon />
                    Guardar sesión actual &ldquo;{sessionSearchText.trim()}&rdquo;
                  </button>
                )}
              </div>,
              document.body
            )}
          {/* Session name input */}
          <div className="session__manager relative">
            <FloatInputForm
              submitIcon={<SaveIcon size={IconSizeEnum.MD} />}
              position={FloatInputPositionEnum.LEFT}
              placeholder={t('Nombre de la sesión')}
              submitOnEnter={true}
              onSubmit={() => {
                const trimmed = sessionNameInput.trim();
                if (!trimmed) return;
                onSaveSession(trimmed);
                setSelectedSessionName(trimmed);
              }}
              onChange={setSessionNameInput}
              value={sessionNameInput}
              hideOutput={true}
              children={
                <Tooltip help={t('Guardar sesión como...')} grow>
                  <IconButton
                    size={IconButtonSizeEnum.MD}
                    shape={IconButtonShapeEnum.SQUARED}
                    type={ButtonTypesEnum.BUTTON}
                    color={IconButtonColorEnum.SECONDARY}
                  >
                    <SaveIcon size={IconSizeEnum.MD} />
                  </IconButton>
                </Tooltip>
              }
            />
          </div>
          {/* Delete session */}
          <Tooltip help={t('Borrar sesión')} grow>
            <IconButton
              shape={IconButtonShapeEnum.SQUARED}
              size={IconButtonSizeEnum.MD}
              color={IconButtonColorEnum.PRIMARY}
              disabled={!selectedSessionName}
              onClick={destroyActiveSession}
            >
              <TrashDeleteIcon size={IconSizeEnum.MD} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="mr-1 flex flex-row items-center gap-2 border-l border-neutral-30 pl-2.5">
          {/* Automatic Organization */}
          <Tooltip help={t('Organizar automáticamente las tarjetas en la grilla')} grow>
            <IconButton
              shape={IconButtonShapeEnum.SQUARED}
              size={IconButtonSizeEnum.MD}
              color={IconButtonColorEnum.DEFAULT}
              disabled={selectedLogos.length === 0 && selectedPeople.length === 0}
              onClick={autoSortCards}
            >
              <SimilarIcon size={IconSizeEnum.MD} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
